import { useState } from 'react'
import { motion } from 'framer-motion'

const PRESETS = [
  { name: 'Eye Clinic',   persona:'FAMILY',      total: 142.50, tax: 11.38,
    items: [{sku:'EXAM-001',description:'Comprehensive Eye Exam',quantity:1,unit_price:95.00,total_price:95.00},{sku:'CLT-090',description:'Contact Lens Trial',quantity:1,unit_price:22.00,total_price:22.00},{sku:'DRP-200',description:'Eye Drops Preservative-Free',quantity:2,unit_price:12.75,total_price:25.50}] },
  { name: 'Home Depot',   persona:'REAL_ESTATE', total: 847.23, tax: 63.82,
    items: [{sku:'206145099',description:'2x6x8 PT Lumber',quantity:24,unit_price:8.47,total_price:203.28},{sku:'100650953',description:'Strong-Tie Joist Hanger',quantity:50,unit_price:1.98,total_price:99.00},{sku:'204276445',description:'5/4x6x16 Deck Board',quantity:18,unit_price:22.97,total_price:413.46}] },
  { name: 'Sony AIBO',   persona:'ROBOTICS',    total: 2999.99, tax: 265.75,
    items: [{sku:'ERS-1000XB1',description:'AIBO Entertainment Robot',quantity:1,unit_price:2799.99,total_price:2799.99},{sku:'AIBO-CLOUD-1',description:'AIBO Cloud Plan 1yr',quantity:1,unit_price:120.00,total_price:120.00}] },
]

export default function POSSimulator({ navigate }) {
  const [preset,    setPreset]    = useState(0)
  const [merchant,  setMerchant]  = useState('My Store')
  const [items,     setItems]     = useState(PRESETS[0].items)
  const [status,    setStatus]    = useState('idle')   // idle | broadcasting | done
  const [broadcastId, setBroadId] = useState(null)

  function loadPreset(idx) {
    setPreset(idx)
    setItems(PRESETS[idx].items)
    setMerchant(PRESETS[idx].name)
  }

  async function broadcast() {
    setStatus('broadcasting')
    const payload = { merchant_name: merchant, total: items.reduce((s,i)=>s+i.total_price,0).toFixed(2), items, persona: PRESETS[preset]?.persona || '' }
    try {
      const res = await fetch('/api/pos/broadcast', {
        method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload),
      })
      const data = res.ok ? await res.json() : {}
      setBroadId(data.broadcast_id || 'BC-' + Math.random().toString(36).substring(2,8).toUpperCase())
    } catch {
      setBroadId('BC-' + Math.random().toString(36).substring(2,8).toUpperCase())
    }
    setStatus('done')
  }

  return (
    <div className="min-h-full bg-[#080c10] px-5 pt-5">
      <div className="sticky top-0 z-10 pb-3 bg-gradient-to-b from-[#080c10] to-transparent">
        <h1 className="text-xl font-black">🏪 Merchant POS</h1>
        <p className="text-xs text-white/30 mt-0.5">Broadcast a receipt to nearby customer devices</p>
      </div>

      {/* Preset picker */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1" style={{scrollbarWidth:'none'}}>
        {PRESETS.map((p,i) => (
          <button key={i} onClick={() => loadPreset(i)}
            className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
              preset===i ? 'bg-[rgba(0,255,135,0.12)] text-[#00ff87] border-[rgba(0,255,135,0.3)]'
                         : 'glass-card text-white/40 hover:text-white/70'}`}>
            {p.name}
          </button>
        ))}
      </div>

      {/* Merchant name */}
      <div className="glass-card p-4 mb-3">
        <label className="text-[10px] font-bold tracking-widest uppercase text-white/30 block mb-2">Merchant Name</label>
        <input value={merchant} onChange={e=>setMerchant(e.target.value)}
          className="w-full bg-transparent text-white font-semibold text-lg outline-none border-b border-white/[0.08] pb-1"
          placeholder="Store name"/>
      </div>

      {/* Items broadcast preview */}
      <div className="glass-card p-0 mb-4 overflow-hidden">
        <div className="px-4 py-3 border-b border-white/[0.06]">
          <span className="text-[11px] font-bold tracking-widest uppercase text-white/30">{items.length} Items to Broadcast</span>
        </div>
        {items.map((item, i) => (
          <div key={i} className={`flex items-start justify-between gap-3 px-4 py-3 ${i<items.length-1?'border-b border-white/[0.04]':''}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">{item.description}</div>
              <div className="sku-mono">SKU {item.sku} · qty {item.quantity}</div>
            </div>
            <div className="text-sm font-bold text-white/60">${item.total_price.toFixed(2)}</div>
          </div>
        ))}
        <div className="flex justify-between items-center px-4 py-3 bg-white/[0.02] border-t border-white/[0.06]">
          <span className="text-sm text-white/40">Total</span>
          <span className="text-lg font-black text-[#00ff87]">
            ${items.reduce((s,i)=>s+i.total_price,0).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Broadcast button */}
      {status === 'idle' && <button onClick={broadcast} className="btn-green mb-2">📡 Broadcast to Live Channel</button>}

      {status === 'broadcasting' && (
        <div className="flex items-center justify-center gap-3 py-4">
          <motion.div className="w-5 h-5 border-2 border-[#00ff87] border-t-transparent rounded-full"
            animate={{rotate:360}} transition={{duration:0.8,repeat:Infinity,ease:'linear'}}/>
          <span className="text-sm text-white/40">Broadcasting receipt…</span>
        </div>
      )}

      {status === 'done' && broadcastId && (
        <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="glass-card p-5 mb-3">
          <div className="text-[#00ff87] font-black text-lg mb-1">✓ Receipt Broadcasted</div>
          <div className="text-xs text-white/30 mb-3">Waiting for customer to suck…</div>
          <div className="detail-row">
            <span className="text-white/40 text-sm">Broadcast ID</span>
            <span className="font-mono text-xs text-[#00ff87]">{broadcastId}</span>
          </div>
          <div className="detail-row">
            <span className="text-white/40 text-sm">Channel</span>
            <span className="font-mono text-xs">/api/pos/latest</span>
          </div>
          <button onClick={() => setStatus('idle')} className="btn-ghost mt-4">Broadcast Again</button>
        </motion.div>
      )}
      <div className="h-4"/>
    </div>
  )
}
